import React, { useEffect, useState } from 'react';
import { generatePath, Link, useParams } from 'react-router-dom';
import api from "../../services/api";
import { DateFormat, FlashMessage } from "../assets/utils";
import Modal from "./Modal";
import BarProgression from "./BarProgression";
import ROUTES from "../../route";
import Picture from "./BookPagePresentation/Picture";

const Book = () => {
    const { id } = useParams();
    const [book, setBook] = useState();
    const [flashMessage, setFlashMessage] = useState(null);
    const [note, setNote] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);

    const showMessage = (message) => setFlashMessage(message);
    const handleCloseFlashMessage = () => setFlashMessage(null);

    const updateRating = async (newRating) => {
        setBook(prev => ({ ...prev, rating: newRating }));
        try {
            await api.post('/updateRating', { rating: newRating, bookId: id });
            showMessage('Rating updated successfully');
        } catch (error) {
            console.error('Failed to update rating', error);
        }
    };

    useEffect(() => {
        if (!book) {
            (async () => {
                try {
                    const response = await api.get(`/book/${id}`);
                    setBook(response.data);
                } catch (error) {
                    console.error('Failed to fetch the book', error);
                }
            })();
        }
    }, [book, id]);

    if (!book) {
        return (
            <div className="flex items-center justify-center py-24">
                <div className="text-center">
                    <span className="text-4xl animate-pulse">📖</span>
                    <p className="mt-4 text-warm-400 font-medium">Loading…</p>
                </div>
            </div>
        );
    }

    const handleNoteSubmit = async () => {
        try {
            const response = await api.post('/storeNote', { content: note, bookId: id });
            showMessage('Note added successfully');
            setIsModalOpen(false);
            setNote('');
            setBook(prev => ({ ...prev, notes: [...prev.notes, response.data.note] }));
        } catch (error) {
            console.error('Failed to add note', error);
        }
    };

    const renderStars = (rating) =>
        Array.from({ length: 10 }, (_, i) => (
            <button
                key={i}
                onClick={() => updateRating(i + 1)}
                className={`text-2xl transition-colors ${i < rating ? 'text-amber-400' : 'text-warm-200 hover:text-amber-300'}`}
            >
                ★
            </button>
        ));

    const bookPath = book.book?.id ? generatePath(ROUTES.EDIT_BOOK, { id: book.book.id }) : '';
    const authorPath = book.book?.author?.id ? generatePath(ROUTES.AUTHOR_SHOW, { id: book.book.author.id }) : '';

    return (
        <div className="max-w-4xl mx-auto">
            {/* Hero section */}
            <div className="bg-white rounded-2xl border border-warm-200 shadow-sm p-6 sm:p-8 mb-6">
                <div className="flex flex-col sm:flex-row gap-6 sm:gap-8">
                    {book.book.picture && (
                        <div className="flex-shrink-0 w-28 sm:w-36 mx-auto sm:mx-0">
                            <Picture
                                book={book.book}
                                classname="w-full rounded-xl shadow-md object-cover"
                            />
                        </div>
                    )}
                    <div className="flex-grow min-w-0">
                        <h1 className="font-serif text-3xl font-bold text-warm-800 leading-tight mb-2">
                            {book.book.title}
                        </h1>
                        <Link
                            to={{ pathname: authorPath }}
                            state={{ author: book.book.author }}
                            className="text-warm-500 hover:text-warm-700 font-medium transition-colors"
                        >
                            {book.book.author?.name}
                        </Link>
                        <p className="text-warm-300 text-sm mt-1">
                            <DateFormat date={book.book.date_of_publication} />
                        </p>

                        {book.book.genres?.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-4">
                                {book.book.genres.map((genre, i) => (
                                    <span key={i} className="bg-warm-100 text-warm-600 text-xs px-3 py-1 rounded-full">
                                        {genre.name}
                                    </span>
                                ))}
                            </div>
                        )}

                        {/* Rating */}
                        <div className="mt-5">
                            <p className="text-xs text-warm-400 uppercase tracking-wide font-medium mb-1">Your rating</p>
                            <div className="flex gap-0.5">
                                {renderStars(book.rating)}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Progress */}
                <div className="mt-6 pt-6 border-t border-warm-100">
                    <BarProgression book={book} setBook={setBook} />
                </div>
            </div>

            {/* Description */}
            {book.book.description && (
                <div className="bg-white rounded-2xl border border-warm-200 shadow-sm p-8 mb-6">
                    <h2 className="font-serif text-lg font-semibold text-warm-800 mb-3">About this book</h2>
                    <p className="text-warm-600 leading-relaxed text-sm"
                        dangerouslySetInnerHTML={{ __html: book.book.description }}
                    />
                </div>
            )}

            {/* Notes */}
            <div className="bg-white rounded-2xl border border-warm-200 shadow-sm p-8 mb-6">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="font-serif text-lg font-semibold text-warm-800">Notes</h2>
                    <button
                        onClick={() => setIsModalOpen(true)}
                        className="bg-warm-600 hover:bg-warm-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
                    >
                        + Add Note
                    </button>
                </div>

                {book.notes?.length > 0 ? (
                    <div className="space-y-3">
                        {book.notes.map((n, i) => (
                            <div key={i} className="bg-warm-50 border-l-4 border-warm-300 rounded-r-lg p-4">
                                <p className="text-warm-600 leading-relaxed">{n.content}</p>
                                <p className="text-warm-300 text-xs mt-2">
                                    <DateFormat date={n.created_at} />
                                </p>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-warm-300 text-sm italic">No notes yet. Add your first note!</p>
                )}
            </div>

            {/* Actions */}
            <div className="flex gap-3">
                <Link to={{ pathname: bookPath }} state={{ book }}>
                    <button className="bg-warm-100 hover:bg-warm-200 text-warm-700 font-medium px-6 py-2.5 rounded-lg transition-colors text-sm">
                        Edit Book
                    </button>
                </Link>
            </div>

            {flashMessage && (
                <FlashMessage message={flashMessage} duration={2000} onClose={handleCloseFlashMessage} />
            )}

            <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
                <div className="p-8">
                    <h3 className="font-serif text-xl font-semibold text-warm-800 mb-4">Add a Note</h3>
                    <textarea
                        rows="5"
                        className="w-full px-4 py-3 bg-warm-50 border border-warm-200 rounded-lg text-warm-800 text-sm focus:outline-none focus:ring-2 focus:ring-warm-400 resize-none"
                        placeholder="Write your thoughts about this book…"
                        value={note}
                        onChange={e => setNote(e.target.value)}
                    />
                    <button
                        onClick={handleNoteSubmit}
                        className="w-full mt-4 bg-warm-600 hover:bg-warm-700 text-white font-semibold py-3 rounded-lg transition-colors"
                    >
                        Save Note
                    </button>
                </div>
            </Modal>
        </div>
    );
};

export default Book;
